/**
 * Product Portfolio Treemap visualization
 * Features:
 * - Squarified treemap algorithm for optimal rectangle aspect ratios
 * - Metric selector: Revenue, Users, Market Share
 * - Category color coding
 * - Interactive hover with product details
 */

import { useState, useRef, useCallback, useMemo } from 'react';

// Metric types
type MetricType = 'revenue' | 'users' | 'marketShare';

const METRICS: { value: MetricType; label: string }[] = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'users', label: 'Users' },
  { value: 'marketShare', label: 'Market Share' },
];

// Color palette for categories (matching RevenueChart SEGMENT_COLORS)
const CATEGORY_COLORS: Record<string, string> = {
  'Prescription Technology': '#E30B5C',  // Raspberry
  'Distribution': '#10B981',             // Emerald
  'Connectivity': '#F59E0B',             // Amber
  'Patient Engagement': '#EC4899',       // Pink
  'Medical Supplies': '#8B5CF6',         // Violet
  'Oncology': '#14B8A6',                 // Teal
  'Patient Education': '#F97316',        // Orange
  'Analytics': '#06B6D4',                // Cyan
};

const DEFAULT_COLORS = [
  '#E30B5C', '#10B981', '#F59E0B', '#EC4899',
  '#8B5CF6', '#14B8A6', '#F97316', '#06B6D4',
];

export interface TreemapProduct {
  id: string;
  name: string;
  category: string;
  segment?: string;
  revenue?: number | null;
  users?: number | null;
  marketShare?: number | null;
  isEstimated?: boolean;
  confidence?: 'high' | 'medium' | 'low';
  revenueModel?: string;
  isFlagship?: boolean;
}

interface TreemapNode {
  product: TreemapProduct;
  x: number;
  y: number;
  width: number;
  height: number;
  value: number;
}

interface HoverInfo {
  productId: string;
  productName: string;
  category: string;
  segment?: string;
  revenue: number | null;
  users: number | null;
  marketShare: number | null;
  isEstimated: boolean;
  confidence: 'high' | 'medium' | 'low';
  revenueModel?: string;
  isFlagship?: boolean;
  x: number;
  y: number;
}

interface ProductTreemapProps {
  products: TreemapProduct[];
  selectedMetric: MetricType;
  onMetricChange: (metric: MetricType) => void;
  isLoading?: boolean;
  width?: number;
  height?: number;
}

function getCategoryColor(category: string, categoryIndex: number): string {
  const categoryColor = CATEGORY_COLORS[category];
  if (categoryColor) return categoryColor;
  return DEFAULT_COLORS[categoryIndex % DEFAULT_COLORS.length] ?? '#6b7280';
}

function formatMetricValue(value: number | null, metric: MetricType): string {
  if (value === null || value === undefined) return 'N/A';

  switch (metric) {
    case 'revenue':
      if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
      if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
      if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
      return `$${value.toLocaleString()}`;
    case 'users':
      if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
      if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
      return value.toLocaleString();
    case 'marketShare':
      return `${value.toFixed(1)}%`;
    default:
      return value.toLocaleString();
  }
}

function truncateLabel(text: string, maxWidth: number): string {
  // Approximate characters that fit (assuming ~7px per char)
  const maxChars = Math.floor(maxWidth / 7);
  if (text.length <= maxChars) return text;
  return text.slice(0, maxChars - 2) + '...';
}

// Squarified treemap algorithm
function squarify(
  products: TreemapProduct[],
  metric: MetricType,
  containerWidth: number,
  containerHeight: number
): TreemapNode[] {
  // Filter products with valid metric values
  const validProducts = products.filter(p => {
    const value = p[metric];
    return value !== null && value !== undefined && value > 0;
  });

  if (validProducts.length === 0) return [];

  // Sort by value descending (largest first for better layouts)
  const sorted = [...validProducts].sort((a, b) => {
    const aVal = a[metric] ?? 0;
    const bVal = b[metric] ?? 0;
    return (bVal as number) - (aVal as number);
  });

  // Calculate total
  const total = sorted.reduce((sum, p) => sum + ((p[metric] ?? 0) as number), 0);

  if (total === 0) return [];

  const nodes: TreemapNode[] = [];
  squarifyRecursive(
    sorted,
    metric,
    total,
    { x: 0, y: 0, width: containerWidth, height: containerHeight },
    nodes
  );

  return nodes;
}

function squarifyRecursive(
  products: TreemapProduct[],
  metric: MetricType,
  totalValue: number,
  container: { x: number; y: number; width: number; height: number },
  nodes: TreemapNode[]
): void {
  if (products.length === 0 || totalValue === 0) return;

  const { x, y, width, height } = container;
  const area = width * height;

  // For single item, fill container
  if (products.length === 1) {
    const product = products[0];
    if (product) {
      nodes.push({
        product,
        x,
        y,
        width,
        height,
        value: (product[metric] ?? 0) as number
      });
    }
    return;
  }

  // Determine layout direction based on aspect ratio
  const isWide = width >= height;
  const shortSide = isWide ? height : width;

  // Find optimal split point using worst aspect ratio
  let row: TreemapProduct[] = [];
  let rowValue = 0;
  let bestWorst = Infinity;

  for (let i = 0; i < products.length; i++) {
    const testRow = products.slice(0, i + 1);
    const testValue = testRow.reduce((sum, p) => sum + ((p[metric] ?? 0) as number), 0);

    if (testValue === 0) continue;

    const testArea = (testValue / totalValue) * area;
    const testLength = testArea / shortSide;

    // Calculate worst aspect ratio for this row
    let worst = 0;
    for (const p of testRow) {
      const pValue = (p[metric] ?? 0) as number;
      if (pValue === 0 || testValue === 0) continue;
      const pArea = (pValue / testValue) * testArea;
      const pLength = pArea / testLength;
      if (testLength > 0 && pLength > 0) {
        const aspectRatio = Math.max(testLength / pLength, pLength / testLength);
        worst = Math.max(worst, aspectRatio);
      }
    }

    if (worst <= bestWorst || worst === 0) {
      bestWorst = worst;
      row = testRow;
      rowValue = testValue;
    } else {
      break;
    }
  }

  if (row.length === 0) return;

  // Lay out the row
  const rowArea = (rowValue / totalValue) * area;
  const rowLength = rowArea / shortSide;
  let offset = 0;

  for (const p of row) {
    const pValue = (p[metric] ?? 0) as number;
    if (pValue === 0 || rowValue === 0) continue;

    const pArea = (pValue / rowValue) * rowArea;
    const pLength = pArea / rowLength;

    if (isWide) {
      nodes.push({
        product: p,
        x: x,
        y: y + offset,
        width: rowLength,
        height: pLength,
        value: pValue
      });
    } else {
      nodes.push({
        product: p,
        x: x + offset,
        y: y,
        width: pLength,
        height: rowLength,
        value: pValue
      });
    }
    offset += pLength;
  }

  // Recurse for remaining products
  const remaining = products.slice(row.length);
  if (remaining.length > 0) {
    const remainingValue = totalValue - rowValue;
    const newContainer = isWide
      ? { x: x + rowLength, y, width: width - rowLength, height }
      : { x, y: y + rowLength, width, height: height - rowLength };

    squarifyRecursive(remaining, metric, remainingValue, newContainer, nodes);
  }
}

export function ProductTreemap({
  products,
  selectedMetric,
  onMetricChange,
  isLoading = false,
  width = 700,
  height = 400,
}: ProductTreemapProps) {
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Get unique categories for legend
  const uniqueCategories = useMemo(() => {
    const cats = new Set(products.map(p => p.category));
    return Array.from(cats);
  }, [products]);

  // Calculate treemap layout
  const { nodes, missingCount } = useMemo(() => {
    const validProducts = products.filter(p => {
      const value = p[selectedMetric];
      return value !== null && value !== undefined && value > 0;
    });
    const missing = products.length - validProducts.length;
    const layoutNodes = squarify(products, selectedMetric, width, height);
    return { nodes: layoutNodes, missingCount: missing };
  }, [products, selectedMetric, width, height]);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || nodes.length === 0) return;

    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Find which node the mouse is over
    const hoveredNode = nodes.find(node =>
      x >= node.x && x <= node.x + node.width &&
      y >= node.y && y <= node.y + node.height
    );

    if (hoveredNode) {
      setHoverInfo({
        productId: hoveredNode.product.id,
        productName: hoveredNode.product.name,
        category: hoveredNode.product.category,
        segment: hoveredNode.product.segment,
        revenue: hoveredNode.product.revenue ?? null,
        users: hoveredNode.product.users ?? null,
        marketShare: hoveredNode.product.marketShare ?? null,
        isEstimated: hoveredNode.product.isEstimated ?? false,
        confidence: hoveredNode.product.confidence ?? 'medium',
        revenueModel: hoveredNode.product.revenueModel,
        isFlagship: hoveredNode.product.isFlagship,
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    } else {
      setHoverInfo(null);
    }
  }, [nodes]);

  const handleMouseLeave = useCallback(() => {
    setHoverInfo(null);
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 w-48 bg-gray-200 rounded mb-4" />
          <div className="h-[400px] bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Portfolio</h3>
        <p className="text-gray-500 text-center py-8">No product data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 relative">
      {/* Header with metric selector */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Product Portfolio</h3>

        {/* Metric selector toggle */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {METRICS.map((metric) => (
            <button
              key={metric.value}
              onClick={() => onMetricChange(metric.value)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                selectedMetric === metric.value
                  ? 'bg-white text-primary-700 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {metric.label}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs mb-4">
        {uniqueCategories.map((category, i) => (
          <div key={category} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: getCategoryColor(category, i) }}
            />
            <span className="text-gray-600">{category}</span>
          </div>
        ))}
      </div>

      {/* SVG Treemap */}
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="overflow-visible"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {nodes.map((node) => {
          const isHovered = hoverInfo?.productId === node.product.id;
          const categoryIndex = uniqueCategories.indexOf(node.product.category);
          const color = getCategoryColor(node.product.category, categoryIndex);

          return (
            <g key={node.product.id}>
              {/* Rectangle */}
              <rect
                x={node.x + 1}
                y={node.y + 1}
                width={Math.max(0, node.width - 2)}
                height={Math.max(0, node.height - 2)}
                fill={isHovered ? color : `${color}DD`}
                rx={3}
                className="transition-all duration-150 cursor-pointer"
                stroke={isHovered ? '#1F2937' : 'transparent'}
                strokeWidth={isHovered ? 2 : 0}
              />

              {/* Labels (only if rectangle is large enough) */}
              {node.width > 60 && node.height > 40 && (
                <>
                  {/* Product name */}
                  <text
                    x={node.x + node.width / 2}
                    y={node.y + node.height / 2 - 8}
                    textAnchor="middle"
                    className="text-xs font-medium fill-white pointer-events-none"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                  >
                    {truncateLabel(node.product.name, node.width - 16)}
                  </text>
                  {/* Metric value */}
                  <text
                    x={node.x + node.width / 2}
                    y={node.y + node.height / 2 + 8}
                    textAnchor="middle"
                    className="text-[10px] fill-white opacity-90 pointer-events-none"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                  >
                    {formatMetricValue(node.value, selectedMetric)}
                  </text>
                  {/* Flagship indicator */}
                  {node.product.isFlagship && node.height > 60 && (
                    <text
                      x={node.x + node.width / 2}
                      y={node.y + node.height / 2 + 22}
                      textAnchor="middle"
                      className="text-[9px] fill-yellow-200 pointer-events-none"
                      style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                    >
                      Flagship
                    </text>
                  )}
                </>
              )}
              {/* Small rectangle - just show initial */}
              {node.width > 30 && node.width <= 60 && node.height > 25 && (
                <text
                  x={node.x + node.width / 2}
                  y={node.y + node.height / 2 + 4}
                  textAnchor="middle"
                  className="text-[10px] font-medium fill-white pointer-events-none"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  {node.product.name.split(' ').map(w => w[0]).join('').slice(0, 3)}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Missing data note */}
      {missingCount > 0 && (
        <p className="text-xs text-gray-500 mt-3">
          {missingCount} product{missingCount > 1 ? 's' : ''} without{' '}
          {METRICS.find(m => m.value === selectedMetric)?.label.toLowerCase()} data
        </p>
      )}

      {/* Tooltip */}
      {hoverInfo && (
        <div
          className="absolute bg-white border border-gray-200 rounded-lg shadow-lg p-3 pointer-events-none z-10 min-w-[200px]"
          style={{
            left: Math.min(hoverInfo.x + 15, width - 220),
            top: Math.max(hoverInfo.y - 10, 10),
            transform: hoverInfo.y > height / 2 ? 'translateY(-100%)' : 'translateY(0)',
          }}
        >
          {/* Product header */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-gray-900">
              {hoverInfo.productName}
            </span>
            {hoverInfo.isFlagship && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">
                Flagship
              </span>
            )}
          </div>

          {/* Category & Segment */}
          <div className="text-xs text-gray-500 mb-2">
            <span className="inline-flex items-center gap-1">
              <div
                className="w-2 h-2 rounded-sm"
                style={{ backgroundColor: getCategoryColor(hoverInfo.category, uniqueCategories.indexOf(hoverInfo.category)) }}
              />
              {hoverInfo.category}
            </span>
            {hoverInfo.segment && (
              <span className="ml-2 text-gray-400">| {hoverInfo.segment}</span>
            )}
          </div>

          {/* Metrics grid */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-gray-500">Revenue:</span>
              <span className={`ml-1 font-medium ${selectedMetric === 'revenue' ? 'text-primary-600' : 'text-gray-700'}`}>
                {formatMetricValue(hoverInfo.revenue, 'revenue')}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Users:</span>
              <span className={`ml-1 font-medium ${selectedMetric === 'users' ? 'text-primary-600' : 'text-gray-700'}`}>
                {formatMetricValue(hoverInfo.users, 'users')}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Market Share:</span>
              <span className={`ml-1 font-medium ${selectedMetric === 'marketShare' ? 'text-primary-600' : 'text-gray-700'}`}>
                {formatMetricValue(hoverInfo.marketShare, 'marketShare')}
              </span>
            </div>
            {hoverInfo.revenueModel && (
              <div>
                <span className="text-gray-500">Model:</span>
                <span className="ml-1 font-medium text-gray-700 capitalize">
                  {hoverInfo.revenueModel.replace('_', ' ')}
                </span>
              </div>
            )}
          </div>

          {/* Confidence indicator */}
          {hoverInfo.isEstimated && (
            <div className="mt-2 text-xs text-amber-600">
              Estimated ({hoverInfo.confidence} confidence)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProductTreemap;
